import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, AlertTriangle, ShieldCheck, X, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/adminApi";

/**
 * BulkUploadModal — two-step preview → apply UI for Tab 1 CSV upload.
 * Backend contract:
 *   POST /admin/jurisdictions/bulk-upload-preview  { collection, csv_text }
 *   POST /admin/jurisdictions/bulk-upload-apply    { collection, csv_text, mode, confirm_force }
 *
 * SoT protection: rows currently marked source_of_truth="admin_edited" get a
 * visible amber badge in the diff. Default apply mode ("skip_admin_edited")
 * leaves those rows untouched. Force mode requires the admin to type "FORCE".
 */
export default function BulkUploadModal({ open, onClose, collection, onApplied }) {
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  const [forceInput, setForceInput] = useState("");
  const fileRef = useRef(null);

  const reset = () => {
    setCsvText(""); setPreview(null); setShowForceConfirm(false); setForceInput("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => { reset(); onClose(); };

  const onFile = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    if (f.size > 500_000) { toast.error("CSV too large (>500KB)"); return; }
    const text = await f.text();
    setCsvText(text);
    setPreview(null);
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/jurisdictions/bulk-upload-preview",
        { collection, csv_text: text });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const apply = async (mode) => {
    if (!csvText) return;
    if (mode === "force_all" && forceInput.trim().toUpperCase() !== "FORCE") {
      toast.error("Type FORCE to confirm the override");
      return;
    }
    setApplying(true);
    try {
      const { data } = await adminApi.post("/admin/jurisdictions/bulk-upload-apply", {
        collection, csv_text: csvText, mode,
        confirm_force: mode === "force_all",
      });
      // 30-second Undo toast — reuses the revert endpoint. Only offered when
      // rows were actually updated. Skip-mode is safe here because no row
      // could have been admin-edited in the seconds after this apply.
      if (data.updated_count > 0 && data.id) {
        toast.success(
          `Applied: ${data.updated_count} updated, ${data.skipped_sot_count} SoT-skipped, ${data.rejected_count} rejected`,
          {
            duration: 30_000,
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  const { data: rev } = await adminApi.post(
                    `/admin/jurisdictions/bulk-upload-revert/${data.id}`,
                    { mode: "skip_admin_edited" },
                  );
                  toast.success(`Reverted ${rev.updated_count} row(s)`);
                  onApplied?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Undo failed");
                }
              },
            },
          },
        );
      } else {
        toast.success(
          `Applied: ${data.updated_count} updated, ${data.skipped_sot_count} SoT-skipped, ${data.rejected_count} rejected`,
        );
      }
      onApplied?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  const s = preview?.summary;
  const canSkipApply = s && s.will_update - s.sot_protected > 0;
  const hasSotConflicts = s && s.sot_protected > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-4xl bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)]">
        <DialogHeader>
          <DialogTitle className="text-[var(--rg-text-strong)] flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Bulk CSV Upload — {collection === "tax" ? "Sales Tax" : "Privacy Law"}
          </DialogTitle>
        </DialogHeader>

        {!preview && (
          <div className="py-6">
            <p className="text-sm text-[var(--rg-text)] mb-4">
              Select a CSV exported from this table. Only rows changed vs. the current DB will be applied.
              Nothing is written until you confirm on the next screen.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              data-testid="bulk-upload-file"
              className="block w-full text-sm text-[var(--rg-text)] file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-sm file:font-semibold file:bg-[var(--rg-surface)] file:text-[var(--rg-sage)] hover:file:bg-[var(--rg-surface-2)]"
            />
            {loading && (
              <div className="mt-4 text-[var(--rg-muted)] text-sm">
                <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Parsing preview…
              </div>
            )}
          </div>
        )}

        {preview && (
          <div className="max-h-[65vh] overflow-y-auto">
            <SummaryBar summary={preview.summary} />

            <div className="mt-4 space-y-2" data-testid="bulk-upload-diff-list">
              {preview.diffs.filter((d) => d.action !== "no_change").map((d) => (
                <DiffRow key={d.abbreviation} diff={d} />
              ))}
              {preview.diffs.every((d) => d.action === "no_change") && (
                <div className="text-center text-sm text-[var(--rg-muted)] py-6" data-testid="bulk-upload-no-changes">
                  No changes detected. This CSV matches the current DB exactly.
                </div>
              )}
            </div>

            {showForceConfirm && (
              <div className="mt-6 rounded-md border border-[var(--rg-red)] bg-[var(--rg-red-soft)] p-4">
                <p className="text-[var(--rg-red)] font-semibold text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-1.5" />
                  This will overwrite {s.sot_protected} admin-verified row(s). Type <code className="font-mono bg-black/30 px-1">FORCE</code> to confirm.
                </p>
                <Input
                  value={forceInput}
                  onChange={(e) => setForceInput(e.target.value)}
                  placeholder="FORCE"
                  className="mt-3 bg-[var(--rg-bg)] border-[var(--rg-red)] text-[var(--rg-text-strong)]"
                  data-testid="bulk-upload-force-input"
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="border-t border-[var(--rg-border)] pt-4">
          {preview && (
            <div className="flex flex-wrap gap-2 w-full justify-end">
              <Button variant="outline" onClick={close} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]">
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
              {!showForceConfirm && (
                <>
                  <Button
                    onClick={() => apply("skip_admin_edited")}
                    disabled={!canSkipApply || applying}
                    className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold"
                    data-testid="bulk-upload-apply-skip"
                  >
                    <ShieldCheck className="w-4 h-4 mr-1.5" />
                    Apply — skip admin-edited ({s ? s.will_update - s.sot_protected : 0})
                  </Button>
                  {hasSotConflicts && (
                    <Button
                      onClick={() => setShowForceConfirm(true)}
                      variant="outline"
                      className="border-[var(--rg-red)] text-[var(--rg-red)] hover:bg-[var(--rg-red-soft)]"
                      data-testid="bulk-upload-force-toggle"
                    >
                      <AlertTriangle className="w-4 h-4 mr-1.5" />
                      Force update ALL ({s.sot_protected} admin-edited)
                    </Button>
                  )}
                </>
              )}
              {showForceConfirm && (
                <Button
                  onClick={() => apply("force_all")}
                  disabled={forceInput.trim().toUpperCase() !== "FORCE" || applying}
                  className="bg-[var(--rg-red)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-red)] font-semibold"
                  data-testid="bulk-upload-force-apply"
                >
                  {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1.5" />}
                  Force apply ({s.will_update} rows)
                </Button>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryBar({ summary }) {
  const chip = (label, n, color) => (
    <span
      key={label}
      className="inline-block px-2 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wider mr-2 mb-2"
      style={{ color, border: `1px solid ${color}`, background: color + "15" }}
    >
      {n} {label}
    </span>
  );
  return (
    <div className="flex flex-wrap" data-testid="bulk-upload-summary">
      {chip("total", summary.total_csv_rows, "var(--rg-muted)")}
      {chip("will update", summary.will_update, "var(--rg-sage)")}
      {chip("unchanged", summary.unchanged, "var(--rg-muted)")}
      {summary.sot_protected > 0 && chip("SoT-protected", summary.sot_protected, "var(--rg-amber)")}
      {summary.unknown_abbreviations > 0 && chip("unknown abbr", summary.unknown_abbreviations, "var(--rg-red)")}
      {summary.rows_with_errors > 0 && chip("field errors", summary.rows_with_errors, "var(--rg-red)")}
    </div>
  );
}

function DiffRow({ diff }) {
  const isNotInDb = diff.action === "row_not_in_db";
  const border = diff.sot_conflict ? "var(--rg-amber)" : (isNotInDb ? "var(--rg-red)" : "var(--rg-sage)");
  return (
    <div
      className="rounded-sm border p-3 bg-[var(--rg-surface)]"
      style={{ borderColor: border }}
      data-testid={`bulk-diff-${diff.abbreviation}`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[var(--rg-text-strong)] font-semibold">{diff.abbreviation}</span>
        {isNotInDb && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--rg-red)] font-bold border border-[var(--rg-red)] px-1.5 py-0.5 rounded-sm">
            Not in DB — will be rejected
          </span>
        )}
        {diff.sot_conflict && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--rg-amber)] font-bold border border-[var(--rg-amber)] px-1.5 py-0.5 rounded-sm">
            SoT Protected (admin-edited)
          </span>
        )}
        {diff.has_errors && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--rg-red)] font-bold border border-[var(--rg-red)] px-1.5 py-0.5 rounded-sm">
            Field errors
          </span>
        )}
      </div>
      {diff.changes.length > 0 && (
        <ul className="space-y-1 text-[12px]">
          {diff.changes.map((c, i) => (
            <li key={i} className="font-mono text-[var(--rg-text)]">
              <span className="text-[var(--rg-muted)]">{c.field}:</span>{" "}
              <span className="line-through text-[var(--rg-muted)]">{JSON.stringify(c.old)}</span>{" "}
              <span className="text-[var(--rg-sage)]">→</span>{" "}
              <span className="text-[var(--rg-text-strong)]">{JSON.stringify(c.new)}</span>
              {c.error && <span className="text-[var(--rg-red)] ml-2">✗ {c.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
